import type { BaseObj } from '@/objects/BaseObj';
import { PostResponse } from '@/objects/generated/classes/PostResponse';
import baseEntityClasses, { baseEntityNamesThatHasEventID } from '@/objects/generated/baseEntityClasses';

import type { IBaseObjStaticConstructor } from '@/objects/IBaseObjStaticConstructor';
import { IntArray } from '@/objects/generated';
import { StaticBaseDao } from './StaticBaseDao';

export class BaseObjDao extends StaticBaseDao {
    /**
     * Load a list of objects from the server
     * @param type
     * @param eventID
     * @param uids
     * @param detail
     * @returns
     */
    public static async Load<T extends BaseObj>(
        type: IBaseObjStaticConstructor<T>,
        eventID?: number,
        uids?: Array<number>,
        detail?: boolean,
    ): Promise<Array<T>> {
        const basename = BaseObjDao.getClassMappingName(type);
        detail = !!detail;
        let eventpart = '';
        // Only baseEntities that has should include the eventID parameter
        if (baseEntityNamesThatHasEventID.has(basename)) {
            if (!eventID) {
                throw new Error('EventID is required for baseObjects that has the EventID property');
            }
            eventpart = `&eventID=${eventID}`;
            const uidsarr = IntArray.FromJsValues({ Values: uids ?? [] });
            return this.BaseFetchArray<T>(type, `${basename}/Fetch?detailed=${detail}${eventpart}`, 'POST', uidsarr);
        }

        return this.BaseFetchArray<T>(type, `${basename}/Fetch?${this.makeGetStringFromList(uids, 'uids')}&detailed=${detail}${eventpart}`);
    }

    /**
     * We want to load one object or create a new one if the id is a negative number
     * @param type
     * @param id
     * @param eventID
     * @returns
     */
    public static async LoadOrCreateSingle<T extends BaseObj & { EventID?: number; ID: number }>(
        type: IBaseObjStaticConstructor<T>,
        id: number,
        eventID?: number,
    ): Promise<T> {
        if (id < 0) {
            return type.FromJsValues({
                ID: id,
                EventID: eventID,
                EntityState: 'Added', // TODO: Should this be defined in the interface?
            });
        } else {
            const resp = await this.Load<T>(type, eventID, [id], true);

            if (resp.length > 0) {
                return resp[0];
            } else {
                return type._FromServerJson({
                    EntityState: 'Added',
                });
            }
        }
    }

    public static async Save<T extends BaseObj>(type: IBaseObjStaticConstructor<T>, eventID: number, objects: Array<T>) {
        const basename = BaseObjDao.getClassMappingName(type);
        return await this.BaseFetch(PostResponse, `${basename}/Save?eventID=${eventID}`, 'POST', objects);
    }

    public static getClassMappingName = <T extends BaseObj>(type: IBaseObjStaticConstructor<T>) => {
        if (!baseEntityClasses.has(type.NAME_OF_CLASS)) {
            throw new Error('Missing type in baseEntityClasses. Add the MappingName attribute in backend. Typename:' + type.NAME_OF_CLASS);
        }
        return baseEntityClasses.get(type.NAME_OF_CLASS)!;
    };
}
